import { useCallback, useRef, useEffect } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { COLOR_GRADE_FILTERS } from '@/lib/clipUtils'
import { cn } from '@/lib/utils'
import { X, RotateCcw } from 'lucide-react'
import { KeyframeButton } from './KeyframeButton'
import { getKeyframeAtTime, hasKeyframesForProperty } from '@/lib/keyframes'
import { PillSlider, PillToggle } from '@/components/ui/pill-slider'

const GRADE_GROUPS: { label: string; grades: string[] }[] = [
  { label: 'Cinematic', grades: ['cinematic_warm', 'cinematic_cool', 'bleach_bypass', 'noir', 'film_fade'] },
  { label: 'Mood', grades: ['golden_hour', 'moonlight'] },
  { label: 'Stylized', grades: ['vintage', 'sepia', 'bw'] },
  { label: 'Technical', grades: ['desaturated', 'high_contrast'] },
]

const GRADE_LABELS: Record<string, string> = {
  cinematic_warm: 'Warm Cinema',
  cinematic_cool: 'Cool Cinema',
  bleach_bypass: 'Bleach Bypass',
  noir: 'Noir',
  film_fade: 'Film Fade',
  golden_hour: 'Golden Hour',
  moonlight: 'Moonlight',
  vintage: 'Vintage',
  sepia: 'Sepia',
  bw: 'B&W',
  desaturated: 'Desaturated',
  high_contrast: 'Hi Contrast',
}

interface ClipEffects {
  colorGrade?: string
  colorGradeIntensity?: number
  brightness?: number
  contrast?: number
  saturation?: number
  hueRotate?: number
  letterbox?: boolean
  vignette?: number
  grain?: number
  blur?: number
  bloom?: number
  chromaticAberration?: number
  vhs?: boolean
  shake?: number
  fadeIn?: { color: 'black' | 'white'; duration: number }
  fadeOut?: { color: 'black' | 'white'; duration: number }
}

interface FiltersPanelProps {
  clipId: string
  effects: ClipEffects | undefined
  thumbnailUrl?: string
  onClose: () => void
  embedded?: boolean
  animationKeyframes?: import('@/lib/keyframes').KeyframeTracks
  currentClipTime?: number
  onKeyframeToggle?: (property: string, time: number, value: number) => void
}

function GradePreview({ grade, filter, thumbnailUrl, selected }: {
  grade: string
  filter: string
  thumbnailUrl?: string
  selected: boolean
}) {
  return (
    <div className={cn(
      'relative rounded-lg overflow-hidden cursor-pointer transition-all duration-150 group',
      'border-2',
      selected ? 'border-primary shadow-md shadow-primary/20' : 'border-transparent hover:border-border'
    )}>
      <div className="aspect-[16/10] bg-gradient-to-br from-amber-200 via-rose-200 to-indigo-300 relative overflow-hidden">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter }}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full" style={{ filter }}>
            <div className="absolute inset-0 bg-gradient-to-br from-amber-300 via-rose-300 to-indigo-400" />
            <div className="absolute bottom-1 left-1 right-1 h-3 bg-gradient-to-r from-black/20 via-transparent to-black/20 rounded-sm" />
          </div>
        )}
      </div>
      <div className={cn(
        'text-[10px] font-medium text-center py-1 truncate px-1',
        selected ? 'text-primary' : 'text-muted-foreground group-hover:text-secondary-foreground'
      )}>
        {GRADE_LABELS[grade] || grade}
      </div>
    </div>
  )
}


export function FiltersPanel({ clipId, effects, thumbnailUrl, onClose, embedded, animationKeyframes, currentClipTime, onKeyframeToggle }: FiltersPanelProps) {
  const updateEffects = useMutation(api.workflows.updateClipEffects)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const currentGrade = effects?.colorGrade || null
  const currentIntensity = effects?.colorGradeIntensity ?? 1

  const debouncedUpdate = useCallback((
    newEffects: Partial<ClipEffects>,
    clearFlags?: { clearFadeIn?: boolean; clearFadeOut?: boolean },
  ) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      updateEffects({
        clipId: clipId as Id<"workflowClips">,
        effects: newEffects as any,
        ...clearFlags,
      })
    }, 150)
  }, [clipId, updateEffects])

  const handleGradeSelect = useCallback((grade: string | null) => {
    updateEffects({
      clipId: clipId as Id<"workflowClips">,
      effects: { colorGrade: grade || 'none' },
    })
  }, [clipId, updateEffects])

  const handleIntensityChange = useCallback((intensity: number) => {
    debouncedUpdate({ colorGradeIntensity: intensity })
  }, [debouncedUpdate])

  const handleEffectChange = useCallback((key: string, value: number | boolean) => {
    debouncedUpdate({ [key]: value })
  }, [debouncedUpdate])

  const handleFadeChange = useCallback((kind: 'fadeIn' | 'fadeOut', duration: number) => {
    if (duration <= 0) {
      debouncedUpdate({}, kind === 'fadeIn' ? { clearFadeIn: true } : { clearFadeOut: true })
      return
    }
    const currentColor = (kind === 'fadeIn' ? effects?.fadeIn?.color : effects?.fadeOut?.color) ?? 'black'
    debouncedUpdate({ [kind]: { color: currentColor, duration } })
  }, [debouncedUpdate, effects?.fadeIn?.color, effects?.fadeOut?.color])

  const toggleFadeColor = useCallback((kind: 'fadeIn' | 'fadeOut') => {
    const current = kind === 'fadeIn' ? effects?.fadeIn : effects?.fadeOut
    if (!current || current.duration <= 0) return
    const newColor: 'black' | 'white' = current.color === 'black' ? 'white' : 'black'
    debouncedUpdate({ [kind]: { color: newColor, duration: current.duration } })
  }, [debouncedUpdate, effects?.fadeIn, effects?.fadeOut])

  const handleReset = useCallback(() => {
    updateEffects({
      clipId: clipId as Id<"workflowClips">,
      effects: {
        colorGrade: 'none',
        colorGradeIntensity: 0,
        brightness: 1,
        contrast: 1,
        saturation: 1,
        hueRotate: 0,
        vignette: 0,
        blur: 0,
        bloom: 0,
        letterbox: false,
        chromaticAberration: 0,
        vhs: false,
        shake: 0,
      },
      clearFadeIn: true,
      clearFadeOut: true,
    })
  }, [clipId, updateEffects])

  const kfTracks = animationKeyframes ?? { tracks: [] }
  const kfTime = currentClipTime ?? 0

  const makeKfButton = useCallback((property: string, value: number) => {
    if (!onKeyframeToggle) return undefined
    const has = hasKeyframesForProperty(kfTracks, property)
    const atKf = !!getKeyframeAtTime(kfTracks, property, kfTime)
    return (
      <KeyframeButton
        hasKeyframes={has}
        isAtKeyframe={atKf}
        onClick={() => onKeyframeToggle(property, kfTime, value)}
      />
    )
  }, [kfTracks, kfTime, onKeyframeToggle])

  return (
    <div className={cn('flex flex-col', !embedded && 'h-full')}>
      {!embedded && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold text-secondary-foreground uppercase tracking-wider">Filters & Effects</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleReset}
              className="p-1 rounded text-muted-foreground hover:text-secondary-foreground hover:bg-muted transition-colors"
              aria-label="Reset all effects"
            >
              <RotateCcw size={14} />
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded text-muted-foreground hover:text-secondary-foreground hover:bg-muted transition-colors"
              aria-label="Close filters panel"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className={cn('space-y-5', embedded ? 'py-1' : 'flex-1 overflow-y-auto px-4 py-3')}>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Color Grade</span>
          </div>

          <div className="mb-2">
            <button
              onClick={() => handleGradeSelect(null)}
              className={cn(
                'w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
                !currentGrade
                  ? 'bg-primary/5 text-primary border border-primary/20'
                  : 'text-muted-foreground hover:bg-accent border border-transparent'
              )}
            >
              None (Original)
            </button>
          </div>

          {GRADE_GROUPS.map(({ label, grades }) => (
            <div key={label} className="mb-3">
              <span className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">{label}</span>
              <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label={`${label} color grades`}>
                {grades.map((grade) => (
                  <button
                    key={grade}
                    onClick={() => handleGradeSelect(grade)}
                    aria-label={GRADE_LABELS[grade] || grade}
                    aria-pressed={currentGrade === grade}
                    className="text-left"
                  >
                    <GradePreview
                      grade={grade}
                      filter={COLOR_GRADE_FILTERS[grade]}
                      thumbnailUrl={thumbnailUrl}
                      selected={currentGrade === grade}
                    />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {currentGrade && (
          <div className="pb-2 border-b border-border">
            <PillSlider
              label="Grade Intensity"
              value={currentIntensity}
              onChange={handleIntensityChange}
            />
          </div>
        )}

        <div>
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">Color Adjustments</span>
          <div className="space-y-1.5">
            <PillSlider
              label="Brightness"
              value={effects?.brightness ?? 1}
              onChange={(v) => handleEffectChange('brightness', v)}
              min={0.2}
              max={3}
              step={0.01}
              defaultValue={1}
              onReset={() => handleEffectChange('brightness', 1)}
            />
            <PillSlider
              label="Contrast"
              value={effects?.contrast ?? 1}
              onChange={(v) => handleEffectChange('contrast', v)}
              min={0.2}
              max={3}
              step={0.01}
              defaultValue={1}
              onReset={() => handleEffectChange('contrast', 1)}
            />
            <PillSlider
              label="Saturation"
              value={effects?.saturation ?? 1}
              onChange={(v) => handleEffectChange('saturation', v)}
              min={0}
              max={3}
              step={0.01}
              defaultValue={1}
              onReset={() => handleEffectChange('saturation', 1)}
            />
            <PillSlider
              label="Hue Rotate"
              value={effects?.hueRotate ?? 0}
              onChange={(v) => handleEffectChange('hueRotate', v)}
              min={-180}
              max={180}
              step={1}
              defaultValue={0}
              onReset={() => handleEffectChange('hueRotate', 0)}
              unit="°"
              displayMultiplier={1}
            />
          </div>
        </div>

        <div>
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">Effects</span>
          <div className="space-y-1.5">
            <PillSlider
              label="Vignette"
              value={effects?.vignette ?? 0}
              onChange={(v) => handleEffectChange('vignette', v)}
              suffix={makeKfButton('vignette', effects?.vignette ?? 0)}
            />
            <PillSlider
              label="Bloom / Glow"
              value={effects?.bloom ?? 0}
              onChange={(v) => handleEffectChange('bloom', v)}
              suffix={makeKfButton('bloom', effects?.bloom ?? 0)}
            />
            <PillSlider
              label="Blur"
              value={effects?.blur ?? 0}
              onChange={(v) => handleEffectChange('blur', v)}
              max={20}
              step={0.5}
              displayMultiplier={1}
              unit="px"
              suffix={makeKfButton('blur', effects?.blur ?? 0)}
            />
            <PillSlider
              label="Chromatic Ab."
              value={effects?.chromaticAberration ?? 0}
              onChange={(v) => handleEffectChange('chromaticAberration', v)}
              suffix={makeKfButton('chromaticAberration', effects?.chromaticAberration ?? 0)}
            />
            <PillSlider
              label="Shake"
              value={effects?.shake ?? 0}
              onChange={(v) => handleEffectChange('shake', v)}
              suffix={makeKfButton('shake', effects?.shake ?? 0)}
            />
            <div className="h-px bg-border my-1" />
            <PillToggle
              label="Letterbox (2.39:1)"
              value={effects?.letterbox ?? false}
              onChange={(v) => handleEffectChange('letterbox', v)}
            />
            <PillToggle
              label="VHS"
              value={effects?.vhs ?? false}
              onChange={(v) => handleEffectChange('vhs', v)}
            />
          </div>
        </div>

        <div>
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">Fade</span>
          <div className="space-y-1.5">
            <PillSlider
              label="Fade In"
              value={effects?.fadeIn?.duration ?? 0}
              onChange={(v) => handleFadeChange('fadeIn', v)}
              min={0}
              max={3}
              step={0.1}
              displayMultiplier={1}
              displayPrecision={1}
              unit="s"
              suffix={
                <button
                  type="button"
                  onClick={() => toggleFadeColor('fadeIn')}
                  disabled={!effects?.fadeIn || effects.fadeIn.duration <= 0}
                  aria-label={`Fade in color: ${effects?.fadeIn?.color ?? 'black'}`}
                  title={`Fade in color: ${effects?.fadeIn?.color ?? 'black'} — click to toggle`}
                  className={cn(
                    'w-4 h-4 rounded-full border border-neutral-300 shrink-0 transition-opacity',
                    (!effects?.fadeIn || effects.fadeIn.duration <= 0) && 'opacity-30 cursor-default',
                  )}
                  style={{ backgroundColor: effects?.fadeIn?.color === 'white' ? '#fff' : '#000' }}
                />
              }
            />
            <PillSlider
              label="Fade Out"
              value={effects?.fadeOut?.duration ?? 0}
              onChange={(v) => handleFadeChange('fadeOut', v)}
              min={0}
              max={3}
              step={0.1}
              displayMultiplier={1}
              displayPrecision={1}
              unit="s"
              suffix={
                <button
                  type="button"
                  onClick={() => toggleFadeColor('fadeOut')}
                  disabled={!effects?.fadeOut || effects.fadeOut.duration <= 0}
                  aria-label={`Fade out color: ${effects?.fadeOut?.color ?? 'black'}`}
                  title={`Fade out color: ${effects?.fadeOut?.color ?? 'black'} — click to toggle`}
                  className={cn(
                    'w-4 h-4 rounded-full border border-neutral-300 shrink-0 transition-opacity',
                    (!effects?.fadeOut || effects.fadeOut.duration <= 0) && 'opacity-30 cursor-default',
                  )}
                  style={{ backgroundColor: effects?.fadeOut?.color === 'white' ? '#fff' : '#000' }}
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}
